// Heuristic for "should we expose a dedicated Take Photo button on this device?".
//
// The HTML `capture` attribute on <input type="file"> is honored by mobile browsers
// (iOS Safari, Android Chrome/Edge) and silently ignored by desktop browsers, which
// just open a regular file picker. That fallback is functionally fine, but a button
// labeled "Take Photo" would be confusing on a desktop without a usable camera, so
// we gate the affordance on devices that look mobile-like.
//
// Detection rules:
//   - Must have touch input (ontouchstart or navigator.maxTouchPoints > 0).
//   - Must report a coarse pointer via matchMedia when available. Phones/tablets
//     match `(any-pointer: coarse)`; convertible touch laptops typically also
//     report a fine pointer alongside it, and they generally have a webcam, so we
//     still treat them as supported.
//
// Returns false during SSR or in environments without window/navigator so the
// caller can render the desktop-only experience as a safe default.
export function isCameraCaptureSupported(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const hasTouch =
    'ontouchstart' in window ||
    (typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 0);

  if (!hasTouch) {
    return false;
  }

  if (typeof window.matchMedia === 'function') {
    return window.matchMedia('(any-pointer: coarse)').matches;
  }

  return true;
}
